import { Component, Inject, Injector, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, WebhookIntegrationsService } from 'lib-trend-core';

export interface WebhookTestModalData {
  url: string;
  webhookRequests?: Record<string, string>;
}

@Component({
    selector: 'webhook-test-modal',
    templateUrl: './webhook-test-modal.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WebhookTestModalComponent extends AbstractComponent {

  isSending: boolean = false;

  constructor(
    injector: Injector,
    public dialogRef: MatDialogRef<WebhookTestModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: WebhookTestModalData,
    private webhookIntegrationsService: WebhookIntegrationsService,
  ) {
    super(injector);
    this.createForm();
  }

  ngOnInit(): void {
    this.initializeFields();
  }

  private createForm(): void {
    this.formGroup = this.formBuilder.group({
      fields: this.formBuilder.array([]),
    });
  }

  get fieldsArray(): FormArray {
    return this.formGroup.get('fields') as FormArray;
  }

  private initializeFields(): void {
    if (this.data.webhookRequests && Object.keys(this.data.webhookRequests).length > 0) {
      Object.entries(this.data.webhookRequests).forEach(([key, value]) => {
        this.addField(key, String(value));
      });
    }
  }

  addField(key: string = '', value: string = ''): void {
    const fieldGroup = this.formBuilder.group({
      key: [key, Validators.required],
      value: [value, Validators.required],
    });
    this.fieldsArray.push(fieldGroup);
  }

  removeField(index: number): void {
    this.fieldsArray.removeAt(index);
  }

  getFieldGroup(index: number): FormGroup {
    return this.fieldsArray.at(index) as FormGroup;
  }

  close(): void {
    this.dialogRef.close(false);
  }

  sendTest(): void {
    if (this.fieldsArray.length === 0) {
      this.alertService.warning(this.t('integrations.webhookTestModal.warningAddAtLeastOneField'));
      return;
    }

    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup);
      return;
    }

    this.isSending = true;

    const payload: Record<string, string> = {};
    this.fieldsArray.controls.forEach((control) => {
      const key = control.get('key').value;
      const value = control.get('value').value;
      payload[key] = value;
    });

    this.webhookIntegrationsService.testWebhook(this.data.url, payload).subscribe({
      next: (result) => {
        this.alertService.success(this.t('integrations.webhookTestModal.successTestSent'));
        this.dialogRef.close(result);
        this.isSending = false;
      },
      error: (err) => {
        this.alertService.error(err.error?.message || this.t('integrations.webhookTestModal.errorConnectWebhook'));
        this.isSending = false;
      }
    });
  }
}
